import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {BoosterFeedHero} from './BoosterFeedHero';
import {Lightning} from '../../icons';
import {
    BOOSTER_STEP_FEED,
    BOOSTER_STEP_MEMORY,
    BOOSTER_STEP_REVIEW,
    BOOSTER_STEP_USE_IA,
    boosterPathFor,
} from './boosterSteps';

// Orden de recorrido. `BOOSTER_STEP_USE_IA` no entra: es el destino, vive del otro lado del hilo.
const WALK = [BOOSTER_STEP_FEED, BOOSTER_STEP_REVIEW, BOOSTER_STEP_MEMORY];

/**
 * El hilo: los tres pasos y el destino, con el paso actual encendido.
 *
 * Dibuja SÓLO los pasos que la agencia tiene (los mismos que el sidebar — `boosterStepsFor`), pero
 * conserva el número de cada uno: en nivel MEDIO no hay "Revisa", y renumerar dejaría a Memoria
 * como paso 2 en unas agencias y como 3 en otras.
 */
export function BoosterThread({steps, activeStep, clientId, pendingCount, locked = false}) {
    const {t} = useTranslation();
    const walk = WALK.map(key => steps.find(s => s.key === key)).filter(Boolean);
    const dest = steps.find(s => s.key === BOOSTER_STEP_USE_IA);
    // Bloqueado no hay paso actual, y por lo tanto tampoco pasos "hechos": sin cliente elegido no
    // se recorrió nada. El `locked` tiene que apagar los TRES estados, no sólo el activo — si sólo
    // apagara ese, un `activeStep` que llegue igual pintaría de "hecho" a los anteriores.
    const activeIndex = locked ? -1 : walk.findIndex(s => s.key === activeStep);

    if (!walk.length && !dest) return null;

    // Sin cliente elegido el hilo se dibuja pero no navega: cada paso llevaría a esta misma
    // pantalla, y un link que no te mueve se lee como un link roto. Se muestra igual —es el mapa
    // de lo que viene— y por eso son `<span>`, no `<Link>` deshabilitados.
    const Step = locked ? 'span' : Link;
    const stepProps = (step) => (locked ? {} : {to: boosterPathFor(step.key, clientId)});

    return (
        <nav className={`iab-thread ${locked ? '-locked' : ''}`} aria-label={t('ia_booster.thread.aria')}>
            {walk.map((step, i) => {
                const isActive = !locked && step.key === activeStep;
                // "Hecho" = quedó atrás en el recorrido. Con el destino activo (o fuera del
                // recorrido) no hay índice, así que ningún paso se marca como hecho: decir que
                // están completos sería inventar un estado que no medimos.
                const isDone = activeIndex > -1 && i < activeIndex;
                const showBadge = step.key === BOOSTER_STEP_REVIEW && pendingCount > 0;
                return (
                    <React.Fragment key={step.key}>
                        {i > 0 && (
                            <span className={`iab-thread-link ${i === 2 ? '-second' : ''}`} aria-hidden="true"/>
                        )}
                        <Step
                            className={`iab-thread-step ${isActive ? '-active' : ''} ${isDone ? '-done' : ''}`}
                            aria-current={isActive ? 'step' : undefined}
                            {...stepProps(step)}
                        >
                            <span className="iab-thread-node">
                                {step.order}
                                {showBadge && <span className="cmem-pill-badge">{pendingCount}</span>}
                            </span>
                            <span className="iab-thread-label">{t(step.labelKey)}</span>
                        </Step>
                    </React.Fragment>
                );
            })}
            {dest && (
                <span className="iab-thread-aperture">
                    <Step
                        className={`iab-thread-dest ${!locked && activeStep === BOOSTER_STEP_USE_IA ? '-active' : ''}`}
                        {...stepProps(dest)}
                    >
                        <Lightning size={14} weight="fill"/>
                        {t(dest.labelKey)}
                    </Step>
                </span>
            )}
        </nav>
    );
}

/**
 * El escenario del Recorrido: el lienzo oscuro que corona las pantallas del AI Booster.
 *
 * Se monta como hermano de `.section-body`, arriba del contenido de siempre. Sólo aparece en el
 * marco 2.1 — quien decide es el que lo monta, no este componente.
 *
 * El orbe vive SÓLO en el paso Alimenta, como en el mockup: es ahí donde el gesto de soltar
 * material tiene sentido, y es el único paso donde nadie más está pidiendo el brief (en Memoria lo
 * pide `MemoryResumenView`, así que ponerlo también acá sería el mismo request dos veces).
 */
export function BoosterStage({
    clientId,
    clientName,
    step,
    steps,
    pendingCount = 0,
    hasIa = false,
    onIngested,
}) {
    const {t} = useTranslation();
    const [factCount, setFactCount] = useState(null);
    // "Todavía no llegó" y "no lo pude leer" terminan los dos sin número, pero NO son lo mismo: el
    // brief puede tardar varios segundos y durante ese rato un guion quieto en el centro del orbe
    // se lee como "este cliente no tiene memoria". Mientras carga, el guion late.
    const [briefFailed, setBriefFailed] = useState(false);
    const isFeed = step === BOOSTER_STEP_FEED;

    useEffect(() => {
        if (!isFeed || !clientId) return undefined;
        let alive = true;
        // `cache: true`: el escenario entra a este endpoint en CADA visita al paso Alimenta y sólo
        // le lee un `length`. El brief es caro —hasta 300 hechos serializados, más notas, reuniones
        // y conexiones de Drive—, así que ir y volver entre pasos no debería re-pedirlo. Lo que
        // corresponde de fondo es un contador en el backend; queda anotado como deuda (C-8).
        ClientMemoryApi.getMemoryBrief(clientId, {cache: true}).then((data) => {
            if (!alive) return;
            if (data && data.status === 'OK' && data.data) {
                setFactCount((data.data.facts || []).length);
            } else {
                // Falló el brief: el orbe late igual, pero sin número. Un "0" acá diría que el
                // cliente no tiene memoria, que es una afirmación distinta de "no pude leerla".
                setBriefFailed(true);
            }
        }).catch(() => { if (alive) setBriefFailed(true); });
        return () => { alive = false; };
    }, [clientId, isFeed]);

    const headKey = {
        [BOOSTER_STEP_FEED]: 'feed',
        [BOOSTER_STEP_REVIEW]: 'review',
        [BOOSTER_STEP_MEMORY]: 'memory',
    }[step];

    // El arranque de cero (card JCzWrSNo): con la memoria vacía o la cola sin nada, el copy normal
    // describe algo que no está pasando. "Lo que Dashbo encontró espera tu ok" sobre una cola
    // vacía se lee como que algo se perdió; el estado vacío tiene que decir que todavía no hay
    // nada Y de dónde va a venir. `factCount === null` (el brief no cargó) NO es vacío.
    const isEmptyStep = (headKey === 'feed' && factCount === 0)
        || (headKey === 'review' && pendingCount === 0);
    const ledeKey = headKey && `ia_booster.head.${headKey}_lede${isEmptyStep ? '_empty' : ''}`;

    return (
        <div className="iab-stage" data-step={step || 'memoria'}>
            <div className="iab-aurora" aria-hidden="true">
                <i className="iab-au-alimenta"/>
                <i className="iab-au-revisa"/>
                <i className="iab-au-memoria"/>
                <i className="iab-au-usa"/>
            </div>

            <div className="iab-inner">
                <BoosterThread steps={steps} activeStep={step} clientId={clientId}
                               pendingCount={pendingCount}/>

                {headKey && (
                    <>
                        <div className="iab-head">
                            <h2 className="iab-title">
                                {t(`ia_booster.head.${headKey}_title`, {
                                    client: clientName || t('ia_booster.this_client'),
                                    interpolation: {escapeValue: false},
                                })}
                            </h2>
                        </div>
                        <p className="iab-lede">{t(ledeKey)}</p>
                    </>
                )}

                {isFeed && (
                    <BoosterFeedHero
                        clientId={clientId}
                        clientName={clientName}
                        factCount={factCount}
                        briefFailed={briefFailed}
                        pendingCount={pendingCount}
                        hasIa={hasIa}
                        onIngested={onIngested}
                    />
                )}
            </div>
        </div>
    );
}

export default BoosterStage;
